import React from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    id: string;
    /** Current value length, in the same UTF-16 code units `maxLength` counts. */
    length: number;
    maxLength: number;
}

/**
 * Trailing `used/max` indicator for {@link CharacterCountInput}, mounted once
 * the reveal threshold is passed. That is well before the limit is reached, so
 * the live region below is already in the accessibility tree when it gets its
 * first message.
 */
const CharacterCount = ({ id, length, maxLength }: Props) => {
    const { i18next } = useAppContext();

    return (
        <>
            <span
                id={id}
                // The visible text is numeric so it stays narrow enough to sit
                // inside the field; screen readers get the sentence.
                aria-label={i18next.t('Input.CharacterLimit', {
                    characters: Math.max(maxLength - length, 0),
                })}
                className={twMerge(
                    'text-xs tabular-nums',
                    length > maxLength ? 'text-red' : 'text-gray-500',
                )}
            >
                {/* `103/120` is a neutral run — isolate it so it is not
                    reordered against surrounding RTL text. */}
                <bdi>{`${length}/${maxLength}`}</bdi>
            </span>

            {/* Announced once, when the wall is hit. A live counter would
                interrupt the typing echo on every keystroke. `<output>` is a
                polite live region by default, so it needs no explicit role. */}
            <output className="sr-only">
                {length >= maxLength
                    ? i18next.t('Input.CharacterLimitReached')
                    : ''}
            </output>
        </>
    );
};

export default CharacterCount;
